// TIENDA DE PERSONALIZABLES — piezas compartidas entre la tienda, "Mis pedidos"
// y el panel de administración.
//
// Aquí vive todo lo que se repite en los tres sitios: la paleta de estados del
// pedido, la línea de tiempo, los logos de los métodos de pago (SVG propios: la
// CSP de la web no deja cargar imágenes de terceros), el recuadro de fotos que
// acepta pegar con Ctrl+V y el chat del pedido.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { collection, orderBy, query } from 'firebase/firestore';
import { db } from '@/firebase/config';
import { useCollection } from '@/firebase/firestore/use-collection';
import { cn, sanitizeFirestoreData } from '@/lib/utils';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/use-toast';
import { packsShop_sendMessage, uploadPackRequestImage } from '@/lib/actions';
import type { PackOrder, PackOrderMessage, PackOrderStatus } from '@/lib/types';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import {
  Clock, CheckCircle2, XCircle, CreditCard, Hammer, PackageCheck, Boxes, Send,
  Loader2, ImagePlus, X, MessageSquare, Sparkles, Truck, ClipboardPaste,
} from 'lucide-react';

// ─── Estados ────────────────────────────────────────────────────────────────

export const ESTADOS: Record<PackOrderStatus, {
  label: string;
  short: string;
  color: string;       // color base del estado (hex, se usa en style)
  icon: React.ElementType;
  desc: string;
}> = {
  pendiente:      { label: 'Pendiente de revisión', short: 'Pendiente',  color: '#f59e0b', icon: Clock,        desc: 'Estamos revisando tu solicitud.' },
  rechazado:      { label: 'Solicitud rechazada',   short: 'Rechazada',  color: '#ef4444', icon: XCircle,      desc: 'Tu solicitud no ha sido aceptada.' },
  aceptado:       { label: 'Aceptada · pendiente de pago', short: 'A pagar', color: '#38bdf8', icon: CreditCard, desc: 'Elige cómo quieres pagar y realiza el pago.' },
  pago_reportado: { label: 'Comprobando el pago',   short: 'Pago enviado', color: '#a78bfa', icon: Clock,      desc: 'Nos has dicho que has pagado: lo estamos comprobando.' },
  en_proceso:     { label: 'En proceso',            short: 'En proceso', color: '#fbbf24', icon: Hammer,       desc: 'Pago recibido. Estamos creando tu personalizable.' },
  terminado:      { label: 'Terminado',             short: 'Terminado',  color: '#34d399', icon: Sparkles,     desc: 'Tu personalizable ya está creado.' },
  en_mod:         { label: 'Dentro del mod',        short: 'En el mod',  color: '#22d3ee', icon: Boxes,        desc: 'Ya está incluido en el mod del servidor.' },
  entregado:      { label: 'Entregado',             short: 'Entregado',  color: '#10b981', icon: PackageCheck, desc: '¡Listo! Tu personalizable está entregado.' },
  cancelado:      { label: 'Cancelado',             short: 'Cancelado',  color: '#94a3b8', icon: XCircle,      desc: 'Este pedido se ha cancelado.' },
};

/** Pasos que se pintan en la línea de tiempo, en orden. */
export const PASOS: PackOrderStatus[] = [
  'pendiente', 'aceptado', 'pago_reportado', 'en_proceso', 'terminado', 'en_mod', 'entregado',
];

/** Estados que el admin puede fijar a mano, en el orden en que se avanza. */
export const AVANCE: { key: 'paid' | PackOrderStatus; label: string; from: PackOrderStatus[] }[] = [
  { key: 'paid',      label: 'He recibido el pago', from: ['aceptado', 'pago_reportado'] },
  { key: 'terminado', label: 'Terminado',           from: ['en_proceso'] },
  { key: 'en_mod',    label: 'Dentro del mod',      from: ['terminado'] },
  { key: 'entregado', label: 'Entregado',           from: ['en_mod', 'terminado'] },
];

export function hexToRgba(hex: string, alpha: number) {
  const h = (hex || '#888888').replace('#', '');
  const r = parseInt(h.substring(0, 2), 16);
  const g = parseInt(h.substring(2, 4), 16);
  const b = parseInt(h.substring(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

export function EstadoBadge({ status, className }: { status: PackOrderStatus; className?: string }) {
  const e = ESTADOS[status] || ESTADOS.pendiente;
  const Icon = e.icon;
  return (
    <span
      className={cn('inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-bold', className)}
      style={{ color: e.color, borderColor: hexToRgba(e.color, 0.35), background: hexToRgba(e.color, 0.12) }}
    >
      <Icon className="h-3.5 w-3.5" /> {e.short}
    </span>
  );
}

/**
 * Línea de tiempo del pedido. Los estados terminales (rechazado / cancelado)
 * no tienen recorrido que enseñar, así que se resuelven con un solo aviso.
 */
export function EstadoTimeline({ order, compact = false }: { order: PackOrder; compact?: boolean }) {
  const st = order.status;
  // Color de los pasos aún no alcanzados.
  const apagadoBorde = 'rgba(255,255,255,0.12)';
  const apagadoTexto = 'rgba(255,255,255,0.3)';
  const apagadoLinea = 'rgba(255,255,255,0.08)';

  if (st === 'rechazado' || st === 'cancelado') {
    const e = ESTADOS[st];
    const Icon = e.icon;
    return (
      <div
        className="flex items-start gap-3 rounded-2xl border p-4"
        style={{ borderColor: hexToRgba(e.color, 0.3), background: hexToRgba(e.color, 0.08) }}
      >
        <Icon className="mt-0.5 h-5 w-5 shrink-0" style={{ color: e.color }} />
        <div className="min-w-0">
          <p className="font-bold" style={{ color: e.color }}>{e.label}</p>
          {st === 'rechazado' && order.rejectionReason && (
            <p className="mt-1 whitespace-pre-wrap text-sm text-muted-foreground">
              <span className="font-semibold text-foreground">Motivo: </span>{order.rejectionReason}
            </p>
          )}
        </div>
      </div>
    );
  }

  const idx = PASOS.indexOf(st);

  return (
    <div className={cn('flex items-start', compact && 'scale-95 origin-left')}>
      {PASOS.map((p, i) => {
        const e = ESTADOS[p];
        const hecho = i <= idx;
        const actual = i === idx;
        const Icon = e.icon;
        return (
          <div key={p} className="flex flex-1 items-start">
            <div className="flex min-w-0 flex-1 flex-col items-center text-center">
              <span
                className={cn(
                  'relative grid h-8 w-8 shrink-0 place-items-center rounded-full border-2 transition-colors',
                  actual && 'ring-4',
                )}
                style={{
                  borderColor: hecho ? e.color : apagadoBorde,
                  background: hecho ? hexToRgba(e.color, 0.18) : 'transparent',
                  color: hecho ? e.color : apagadoTexto,
                  boxShadow: actual ? `0 0 0 4px ${hexToRgba(e.color, 0.14)}` : undefined,
                }}
              >
                <Icon className="h-4 w-4" />
                {actual && (
                  <span
                    className="absolute inset-0 animate-ping rounded-full"
                    style={{ background: hexToRgba(e.color, 0.25) }}
                  />
                )}
              </span>
              <span
                className="mt-1.5 hidden max-w-[92px] text-[11px] leading-tight sm:block"
                style={{ color: hecho ? e.color : apagadoTexto }}
              >
                {e.short}
              </span>
            </div>
            {i < PASOS.length - 1 && (
              <span
                className="mt-4 h-[3px] flex-1 rounded-full"
                style={{ background: i < idx ? hexToRgba(ESTADOS[PASOS[i + 1]].color, 0.6) : apagadoLinea }}
              />
            )}
          </div>
        );
      })}
    </div>
  );
}

// ─── Logos de los métodos de pago ───────────────────────────────────────────
// Van dibujados a mano: la web no puede cargar imágenes externas y así se ven
// nítidos a cualquier tamaño.

export function BizumLogo({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 150 46" className={className} role="img" aria-label="Bizum">
      <defs>
        <linearGradient id="bizum-g" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#00C2FF" />
          <stop offset="55%" stopColor="#0091FF" />
          <stop offset="100%" stopColor="#7B4BFF" />
        </linearGradient>
      </defs>
      {/* Isotipo: las dos "olas" del envío de dinero */}
      <path d="M8 30c6-13 14-13 20 0s14 13 20 0" fill="none" stroke="url(#bizum-g)" strokeWidth="5.5" strokeLinecap="round" />
      <text
        x="60" y="33"
        fontFamily="Poppins, Inter, system-ui, sans-serif"
        fontSize="27" fontWeight="700" letterSpacing="-0.5"
        fill="url(#bizum-g)"
      >
        bizum
      </text>
    </svg>
  );
}

export function TransferenciaLogo({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 150 46" className={className} role="img" aria-label="Transferencia bancaria">
      <defs>
        <linearGradient id="transf-g" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#facc15" />
          <stop offset="100%" stopColor="#f59e0b" />
        </linearGradient>
      </defs>
      {/* Isotipo: frontón de banco */}
      <g stroke="url(#transf-g)" strokeWidth="3" strokeLinecap="round" fill="none">
        <path d="M6 19l14-9 14 9" />
        <path d="M10 21v11M20 21v11M30 21v11" />
        <path d="M6 35h28" />
      </g>
      <text
        x="42" y="27"
        fontFamily="Poppins, Inter, system-ui, sans-serif"
        fontSize="15" fontWeight="700"
        fill="url(#transf-g)"
      >
        Transferencia
      </text>
      <text
        x="42" y="39"
        fontFamily="Poppins, Inter, system-ui, sans-serif"
        fontSize="11" fontWeight="500"
        fill="rgba(255,255,255,0.5)"
      >
        bancaria
      </text>
    </svg>
  );
}

// ─── Recuadro de fotos con pegado desde el portapapeles ─────────────────────

/**
 * Zona de fotos que acepta las tres vías: pegar con Ctrl+V, arrastrar y soltar,
 * o el selector de archivos. El listener de `paste` se engancha a `window` —como
 * en el editor de la galería [103]— para que funcione sin tener el foco puesto
 * en el recuadro, pero no intercepta si estás pegando texto dentro de un campo.
 */
export function ImagePasteZone({
  urls,
  onChange,
  max = 20,
  active = true,
  compact = false,
}: {
  urls: string[];
  onChange: (urls: string[]) => void;
  max?: number;
  /** Solo el formulario visible debe capturar el Ctrl+V global. */
  active?: boolean;
  compact?: boolean;
}) {
  const { toast } = useToast();
  const [uploading, setUploading] = useState(0);
  const [dragging, setDragging] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);
  // El listener global es estable; lee siempre el último valor por referencia.
  const urlsRef = useRef(urls);
  urlsRef.current = urls;

  const subir = useCallback(async (files: File[]) => {
    const libres = max - urlsRef.current.length;
    if (libres <= 0) {
      toast({ title: 'Máximo alcanzado', description: `No puedes añadir más de ${max} fotos.`, variant: 'destructive' });
      return;
    }
    const elegidos = files.filter((f) => f.type.startsWith('image/')).slice(0, libres);
    if (elegidos.length === 0) return;

    setUploading((n) => n + elegidos.length);
    const nuevas: string[] = [];
    for (const f of elegidos) {
      const res = await uploadPackRequestImage(f);
      if (res.success && res.url) nuevas.push(res.url);
      else toast({ title: 'Error al subir', description: res.message, variant: 'destructive' });
      setUploading((n) => n - 1);
    }
    if (nuevas.length) onChange([...urlsRef.current, ...nuevas].slice(0, max));
  }, [max, onChange, toast]);

  useEffect(() => {
    if (!active) return;
    const onPaste = (e: ClipboardEvent) => {
      const target = e.target as HTMLElement | null;
      const enCampo = !!target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA');
      const ficheros = Array.from(e.clipboardData?.items || [])
        .filter((i) => i.kind === 'file' && i.type.startsWith('image/'))
        .map((i) => i.getAsFile())
        .filter(Boolean) as File[];
      // Si no hay imagen en el portapapeles dejamos pasar el pegado normal;
      // pegar una captura dentro del textarea SÍ debe añadirla.
      if (!ficheros.length) return;
      if (enCampo && target?.tagName === 'INPUT') return;
      e.preventDefault();
      subir(ficheros);
    };
    window.addEventListener('paste', onPaste);
    return () => window.removeEventListener('paste', onPaste);
  }, [active, subir]);

  const quitar = (i: number) => onChange(urls.filter((_, idx) => idx !== i));

  return (
    <div className="space-y-2">
      <div
        onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={(e) => { e.preventDefault(); setDragging(false); subir(Array.from(e.dataTransfer.files || [])); }}
        onClick={() => fileRef.current?.click()}
        className={cn(
          'group relative cursor-pointer rounded-2xl border-2 border-dashed transition-all',
          compact ? 'p-3' : 'p-5',
          dragging ? 'border-primary bg-primary/10' : 'border-white/12 hover:border-primary/50 hover:bg-white/[0.03]',
        )}
      >
        <div className="flex flex-col items-center gap-1.5 text-center">
          <div className="flex items-center gap-2 text-primary">
            {uploading > 0 ? <Loader2 className="h-5 w-5 animate-spin" /> : <ClipboardPaste className="h-5 w-5" />}
            <ImagePlus className="h-5 w-5" />
          </div>
          <p className={cn('font-semibold', compact ? 'text-xs' : 'text-sm')}>
            {uploading > 0 ? `Subiendo ${uploading} foto(s)…` : 'Pega tus fotos aquí con Ctrl + V'}
          </p>
          {!compact && (
            <p className="text-xs text-muted-foreground">
              También puedes arrastrarlas o hacer clic para elegirlas · {urls.length}/{max}
            </p>
          )}
        </div>
      </div>
      <input
        ref={fileRef}
        type="file"
        accept="image/*"
        multiple
        className="hidden"
        onChange={(e) => { subir(Array.from(e.target.files || [])); if (fileRef.current) fileRef.current.value = ''; }}
      />

      {urls.length > 0 && (
        <div className="grid grid-cols-3 gap-2 sm:grid-cols-4">
          {urls.map((u, i) => (
            <div key={`${u}-${i}`} className="group relative aspect-square overflow-hidden rounded-xl border border-white/10">
              <img src={u} alt="" className="h-full w-full object-cover" />
              <button
                type="button"
                onClick={() => quitar(i)}
                className="absolute right-1 top-1 grid h-6 w-6 place-items-center rounded-full bg-black/75 text-white opacity-0 transition-opacity group-hover:opacity-100"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/** Galería de solo lectura para las fotos ya enviadas. */
export function ImageGrid({ urls, className }: { urls?: string[]; className?: string }) {
  const [zoom, setZoom] = useState<string | null>(null);
  if (!urls || urls.length === 0) return null;
  return (
    <>
      <div className={cn('grid grid-cols-3 gap-2 sm:grid-cols-4', className)}>
        {urls.map((u, i) => (
          <button
            key={`${u}-${i}`}
            type="button"
            onClick={() => setZoom(u)}
            className="aspect-square overflow-hidden rounded-xl border border-white/10 transition-transform hover:scale-[1.03]"
          >
            <img src={u} alt="" className="h-full w-full object-cover" />
          </button>
        ))}
      </div>
      {zoom && (
        <div
          className="fixed inset-0 z-[200] grid place-items-center bg-black/90 p-6"
          onClick={() => setZoom(null)}
        >
          <img src={zoom} alt="" className="max-h-full max-w-full rounded-xl object-contain" />
          <button className="absolute right-5 top-5 grid h-10 w-10 place-items-center rounded-full bg-white/10 text-white">
            <X className="h-5 w-5" />
          </button>
        </div>
      )}
    </>
  );
}

// ─── Chat del pedido ────────────────────────────────────────────────────────

export function OrderChat({ order, asAdmin = false }: { order: PackOrder; asAdmin?: boolean }) {
  const { user } = useAuth();
  const { toast } = useToast();
  const [texto, setTexto] = useState('');
  const [fotos, setFotos] = useState<string[]>([]);
  const [enviando, setEnviando] = useState(false);
  const finRef = useRef<HTMLDivElement>(null);

  const msgQuery = useMemo(
    () => query(collection(db, 'packs_orders', order.id, 'messages'), orderBy('at', 'asc')),
    [order.id],
  );
  const { data: raw, isLoading } = useCollection<PackOrderMessage>(msgQuery);
  const mensajes = useMemo(
    () => (raw || []).map((m) => sanitizeFirestoreData(m) as PackOrderMessage),
    [raw],
  );

  useEffect(() => { finRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [mensajes.length]);

  const cerrado = !order.chatOpen && !asAdmin;

  const enviar = async () => {
    const t = texto.trim();
    if (!t && fotos.length === 0) return;
    setEnviando(true);
    const res = await packsShop_sendMessage(order.id, t, fotos);
    setEnviando(false);
    if (res?.success) { setTexto(''); setFotos([]); }
    else toast({ title: 'No se pudo enviar', description: res?.message, variant: 'destructive' });
  };

  if (cerrado) {
    return (
      <div className="rounded-2xl border border-white/8 bg-card/40 p-6 text-center">
        <MessageSquare className="mx-auto mb-2 h-8 w-8 text-muted-foreground/40" />
        <p className="text-sm text-muted-foreground">
          El chat con el equipo se abre en cuanto confirmemos tu pago.
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-col overflow-hidden rounded-2xl border border-white/8 bg-card/40">
      <div className="flex items-center gap-2 border-b border-white/8 px-4 py-2.5">
        <MessageSquare className="h-4 w-4 text-primary" />
        <span className="text-sm font-bold">Chat del pedido</span>
        <span className="ml-auto text-xs text-muted-foreground">{order.code}</span>
      </div>

      <div className="max-h-[45vh] min-h-[160px] space-y-3 overflow-y-auto p-4">
        {isLoading ? (
          <div className="grid place-items-center py-8"><Loader2 className="h-5 w-5 animate-spin text-primary" /></div>
        ) : mensajes.length === 0 ? (
          <p className="py-8 text-center text-sm text-muted-foreground">
            Todavía no hay mensajes. Escribe si tienes cualquier duda sobre tu personalizable.
          </p>
        ) : (
          mensajes.map((m) => {
            const mio = m.authorId === user?.uid;
            return (
              <div key={m.id} className={cn('flex', mio ? 'justify-end' : 'justify-start')}>
                <div className={cn('max-w-[80%] space-y-2 rounded-2xl px-3.5 py-2.5', mio
                  ? 'rounded-br-md border border-primary/25 bg-primary/15'
                  : 'rounded-bl-md border border-white/8 bg-white/[0.05]')}>
                  <p className={cn('text-[11px] font-bold', m.fromAdmin ? 'text-primary' : 'text-muted-foreground')}>
                    {m.fromAdmin ? 'Administración' : m.authorName}
                  </p>
                  {m.text && <p className="whitespace-pre-wrap break-words text-sm">{m.text}</p>}
                  <ImageGrid urls={m.images} className="!grid-cols-2" />
                  <p className="text-right text-[10px] text-muted-foreground/60">{formatHora(m.at)}</p>
                </div>
              </div>
            );
          })
        )}
        <div ref={finRef} />
      </div>

      <div className="space-y-2 border-t border-white/8 p-3">
        {fotos.length > 0 && (
          <div className="flex gap-2 overflow-x-auto">
            {fotos.map((u, i) => (
              <div key={i} className="relative h-14 w-14 shrink-0 overflow-hidden rounded-lg border border-white/10">
                <img src={u} alt="" className="h-full w-full object-cover" />
                <button
                  type="button"
                  onClick={() => setFotos((p) => p.filter((_, idx) => idx !== i))}
                  className="absolute right-0.5 top-0.5 grid h-4 w-4 place-items-center rounded-full bg-black/80 text-white"
                >
                  <X className="h-2.5 w-2.5" />
                </button>
              </div>
            ))}
          </div>
        )}
        <div className="flex items-end gap-2">
          <ChatFotoBoton onAdd={(u) => setFotos((p) => [...p, u].slice(0, 10))} />
          <Textarea
            rows={1}
            value={texto}
            onChange={(e) => setTexto(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); enviar(); } }}
            placeholder="Escribe un mensaje…"
            className="min-h-[40px] resize-none"
          />
          <Button size="icon" onClick={enviar} disabled={enviando || (!texto.trim() && fotos.length === 0)}>
            {enviando ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
          </Button>
        </div>
      </div>
    </div>
  );
}

function ChatFotoBoton({ onAdd }: { onAdd: (url: string) => void }) {
  const { toast } = useToast();
  const [subiendo, setSubiendo] = useState(false);
  const ref2 = useRef<HTMLInputElement>(null);

  const subir = async (files: FileList | null) => {
    const f = files?.[0];
    if (!f) return;
    setSubiendo(true);
    const res = await uploadPackRequestImage(f);
    setSubiendo(false);
    if (res.success && res.url) onAdd(res.url);
    else toast({ title: 'Error al subir', description: res.message, variant: 'destructive' });
    if (ref2.current) ref2.current.value = '';
  };

  return (
    <>
      <Button type="button" size="icon" variant="ghost" onClick={() => ref2.current?.click()} disabled={subiendo}>
        {subiendo ? <Loader2 className="h-4 w-4 animate-spin" /> : <ImagePlus className="h-4 w-4" />}
      </Button>
      <input ref={ref2} type="file" accept="image/*" className="hidden" onChange={(e) => subir(e.target.files)} />
    </>
  );
}

// ─── Utilidades de formato ──────────────────────────────────────────────────

export function toMs(v: any): number {
  if (!v) return 0;
  if (typeof v === 'string') { const t = Date.parse(v); return Number.isNaN(t) ? 0 : t; }
  if (typeof v === 'number') return v;
  if (typeof v.toMillis === 'function') return v.toMillis();
  if (v.seconds) return v.seconds * 1000;
  return 0;
}

export function formatEur(v?: number | null) {
  const n = Number(v);
  if (!Number.isFinite(n) || n <= 0) return 'A consultar';
  return n.toLocaleString('es-ES', { style: 'currency', currency: 'EUR', minimumFractionDigits: n % 1 === 0 ? 0 : 2 });
}

export function formatFecha(v: any) {
  const ms = toMs(v);
  if (!ms) return '—';
  return new Date(ms).toLocaleDateString('es-ES', { day: 'numeric', month: 'long', year: 'numeric' });
}

export function formatFechaHora(v: any) {
  const ms = toMs(v);
  if (!ms) return '—';
  return new Date(ms).toLocaleString('es-ES', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
}

function formatHora(v: any) {
  const ms = toMs(v);
  if (!ms) return '';
  return new Date(ms).toLocaleString('es-ES', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
}

/** Etiqueta de stock lista para pintar. `-1` = ilimitado. */
export function stockInfo(stock?: number) {
  const s = Number.isFinite(Number(stock)) ? Math.floor(Number(stock)) : -1;
  if (s < 0) return { agotado: false, label: 'Disponible', color: '#34d399', pocas: false };
  if (s === 0) return { agotado: true, label: 'Agotado', color: '#ef4444', pocas: false };
  if (s <= 3) return { agotado: false, label: `¡Solo quedan ${s}!`, color: '#f59e0b', pocas: true };
  return { agotado: false, label: `${s} disponibles`, color: '#34d399', pocas: false };
}

export { Truck, CheckCircle2 };
